import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2, UserPlus } from "lucide-react";
import { API } from "./_apiConfig";

const UsersManagement = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeEditor = async (userId, userName) => {
    if (!window.confirm(`Révoquer les droits d'éditeur de "${userName}" ?`)) return;

    try {
      const response = await fetch(`${API}/admin/users/${userId}/revoke-editor`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Droits d'éditeur révoqués");
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Erreur");
      }
    } catch (error) {
      toast.error("Erreur réseau");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Supprimer l'utilisateur "${userName}" ? Cette action est irréversible.`)) return;

    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Utilisateur supprimé");
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div data-testid="admin-users">
      <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground mb-4 sm:mb-6">
        Utilisateurs
      </h2>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : users.length > 0 ? (
        <div className="rounded-xl border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[120px]">Nom</TableHead>
                <TableHead className="min-w-[150px]">Email</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead>Vérifié</TableHead>
                <TableHead className="min-w-[100px]">Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium text-sm">{user.full_name}</TableCell>
                  <TableCell className="text-sm">{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={user.role === "admin" ? "default" : "secondary"} className="text-xs">
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.email_verified ? (
                      <Badge variant="outline" className="text-green-500 border-green-500 text-xs">Oui</Badge>
                    ) : (
                      <Badge variant="outline" className="text-yellow-500 border-yellow-500 text-xs">Non</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {new Date(user.created_at).toLocaleDateString("fr-FR")}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {user.role === "editor" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRevokeEditor(user.id, user.full_name);
                          }}
                          className="text-orange-500 hover:text-orange-600 hover:bg-orange-500/10"
                          title="Révoquer les droits d'éditeur"
                          data-testid={`revoke-editor-${user.id}`}
                        >
                          <UserPlus className="h-4 w-4 rotate-180" />
                        </Button>
                      )}
                      {user.id !== currentUser?.id && user.role !== "admin" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteUser(user.id, user.full_name);
                          }}
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Aucun utilisateur inscrit.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UsersManagement;
